import { clientToNdc, Plane, Ray as MathRay, rayFromCamera, Vector3, type CameraView } from '@/math'
import { Ray, VoxelMap, Hit, Vec3 } from './voxelTypes'
import type { VoxelReader } from './voxelReaders'
import { Position, isInBounds, posKeyXYZ } from './voxelMath'
import { pickVoxel } from './ddaPicker'
import type { Surface } from '@/core/surface'
import type { Occupancy2D } from '@/core/surface'
import { nearestOccupancyHit, occupancyFromSurface } from '@/core/surface'
import { drawingSurfaceCoordinate, isValidDrawingSurface, type Axis, type DrawingSurfaces } from '@/interactions/drawingSurfaces'

type PlaneHitTarget = {
  normal: Vector3
  constant: number
  name: string
  clamp: (pt: Vector3) => boolean
  isMirror?: boolean
  selectNearSideCell?: boolean
  drawingSurfaceId?: string
}

export type MirrorRaycastMode = 'bounded' | 'extended'

type RaycastPlaneOptions = {
  mirrorMode?: MirrorRaycastMode
}

function nearSideBoundaryCell(originCoord: number, planeCoord: number): number {
  const boundary = Math.round(planeCoord)
  return originCoord < planeCoord ? boundary - 1 : boundary
}

function clampToBoundsRect(axis: Axis, bounds: Position) {
  return (p: Vector3): boolean => {
    if (axis !== 'x' && (p.x < 0 || p.x > bounds.x)) return false
    if (axis !== 'y' && (p.y < 0 || p.y > bounds.y)) return false
    if (axis !== 'z' && (p.z < 0 || p.z > bounds.z)) return false
    return true
  }
}

// Client position type for screen coordinates
export type ClientPosition = {
  x: number
  y: number
}

// Helper to extract client position from pointer event
export const clientPosFromEvent = (event: PointerEvent): ClientPosition => ({
  x: event.clientX,
  y: event.clientY
})


// Convert screen coordinates to ray
export const screenToRay = (
  clientPos: ClientPosition,
  camera: CameraView,
  canvas: HTMLCanvasElement
): Ray => {
  const ndc = clientToNdc(clientPos.x, clientPos.y, canvas.getBoundingClientRect())
  const ray = rayFromCamera(ndc.x, ndc.y, camera)
  return { origin: ray.origin, direction: ray.direction }
}

// Raycast against voxels using fast DDA algorithm
export const raycastVoxels = (
  ray: Ray,
  voxels: VoxelReader,
  bounds?: Position
): Hit | null => {
  const pickResult = pickVoxel(ray.origin, ray.direction, voxels, bounds)

  if (!pickResult) return null

  // Get the voxel data
  const voxel = voxels.get(posKeyXYZ(pickResult.position.x, pickResult.position.y, pickResult.position.z)) ?? undefined

  return {
    position: pickResult.position,
    normal: pickResult.normal,
    distance: ray.origin.distanceTo(pickResult.exactPoint),
    voxel,
    exactPoint: pickResult.exactPoint,
    type: 'voxel'
  }
}
export const raycastPlanes = (
  ray: Ray,
  bounds: Position,
  surfaceOrOcc?: Surface | Occupancy2D,
  mirrors?: { x?: number; y?: number; z?: number },
  drawingSurfaces?: DrawingSurfaces,
  options?: RaycastPlaneOptions,
): Hit | null => {
  const mathRay = new MathRay(ray.origin, ray.direction)
  const mirrorMode = options?.mirrorMode ?? 'bounded'

  const mirrorPlanes: PlaneHitTarget[] = []
  if (mirrors) {
    const addMirror = (axis: 'x' | 'y' | 'z', e?: number) => {
      if (e == null || !Number.isFinite(e)) return
      const len = axis === 'x' ? bounds.x : axis === 'y' ? bounds.y : bounds.z
      const coord = Math.floor(e) + 1
      if (coord <= 0 || coord >= len) return
      const clamp = mirrorMode === 'extended' ? () => true : clampToBoundsRect(axis, bounds)
      if (axis === 'x') {
        mirrorPlanes.push({ normal: new Vector3(1, 0, 0), constant: -coord, name: 'mirror-x+', clamp, isMirror: true, selectNearSideCell: true })
        mirrorPlanes.push({ normal: new Vector3(-1, 0, 0), constant: coord, name: 'mirror-x-', clamp, isMirror: true, selectNearSideCell: true })
      } else if (axis === 'y') {
        mirrorPlanes.push({ normal: new Vector3(0, 1, 0), constant: -coord, name: 'mirror-y+', clamp, isMirror: true, selectNearSideCell: true })
        mirrorPlanes.push({ normal: new Vector3(0, -1, 0), constant: coord, name: 'mirror-y-', clamp, isMirror: true, selectNearSideCell: true })
      } else {
        mirrorPlanes.push({ normal: new Vector3(0, 0, 1), constant: -coord, name: 'mirror-z+', clamp, isMirror: true, selectNearSideCell: true })
        mirrorPlanes.push({ normal: new Vector3(0, 0, -1), constant: coord, name: 'mirror-z-', clamp, isMirror: true, selectNearSideCell: true })
      }
    }
    addMirror('x', mirrors.x)
    addMirror('y', mirrors.y)
    addMirror('z', mirrors.z)
  }

  const drawingSurfacePlanes: PlaneHitTarget[] = []
  if (drawingSurfaces) {
    for (const [id, surface] of Object.entries(drawingSurfaces)) {
      if (!isValidDrawingSurface(surface, bounds)) continue
      const coord = drawingSurfaceCoordinate(surface)
      const clamp = clampToBoundsRect(surface.axis, bounds)
      if (surface.axis === 'x') {
        drawingSurfacePlanes.push({ normal: new Vector3(1, 0, 0), constant: -coord, name: `drawing-surface-${id}-x+`, clamp, drawingSurfaceId: id, selectNearSideCell: true })
        drawingSurfacePlanes.push({ normal: new Vector3(-1, 0, 0), constant: coord, name: `drawing-surface-${id}-x-`, clamp, drawingSurfaceId: id, selectNearSideCell: true })
      } else if (surface.axis === 'y') {
        drawingSurfacePlanes.push({ normal: new Vector3(0, 1, 0), constant: -coord, name: `drawing-surface-${id}-y+`, clamp, drawingSurfaceId: id, selectNearSideCell: true })
        drawingSurfacePlanes.push({ normal: new Vector3(0, -1, 0), constant: coord, name: `drawing-surface-${id}-y-`, clamp, drawingSurfaceId: id, selectNearSideCell: true })
      } else {
        drawingSurfacePlanes.push({ normal: new Vector3(0, 0, 1), constant: -coord, name: `drawing-surface-${id}-z+`, clamp, drawingSurfaceId: id, selectNearSideCell: true })
        drawingSurfacePlanes.push({ normal: new Vector3(0, 0, -1), constant: coord, name: `drawing-surface-${id}-z-`, clamp, drawingSurfaceId: id, selectNearSideCell: true })
      }
    }
  }

  const closestPlaneHit = (planes: PlaneHitTarget[]): Hit | null => {
    let closestHit: Hit | null = null

    for (const planeData of planes) {
      const dotProduct = ray.direction.dot(planeData.normal)
      if (dotProduct >= 0) continue
      const plane = new Plane(planeData.normal, planeData.constant)
      const intersection = new Vector3()
      if (mathRay.intersectPlane(plane, intersection)) {
        if (!planeData.clamp(intersection)) continue
        const epsilon = 0.0001
        let x = Math.floor(intersection.x + epsilon)
        let y = Math.floor(intersection.y + epsilon)
        let z = Math.floor(intersection.z + epsilon)

        if (planeData.selectNearSideCell) {
          // Choose the voxel on the same side as the ray origin relative to the boundary.
          const coord = Math.abs(planeData.constant)
          if (Math.abs(planeData.normal.x) > 0) {
            x = nearSideBoundaryCell(mathRay.origin.x, coord)
          } else if (Math.abs(planeData.normal.y) > 0) {
            y = nearSideBoundaryCell(mathRay.origin.y, coord)
          } else if (Math.abs(planeData.normal.z) > 0) {
            z = nearSideBoundaryCell(mathRay.origin.z, coord)
          }
          // Clamp all axes to bounds
          x = Math.max(0, Math.min(bounds.x - 1, x))
          y = Math.max(0, Math.min(bounds.y - 1, y))
          z = Math.max(0, Math.min(bounds.z - 1, z))
        } else {
          // Standard planes: clamp rounded coordinates to bounds
          x = Math.max(0, Math.min(bounds.x - 1, x))
          y = Math.max(0, Math.min(bounds.y - 1, y))
          z = Math.max(0, Math.min(bounds.z - 1, z))
        }

        const pos = { x, y, z }
        if (isInBounds(pos, bounds)) {
          const distance = ray.origin.distanceTo(intersection)
          if (!closestHit || distance < closestHit.distance) {
            closestHit = {
              position: pos,
              normal: planeData.normal.clone(),
              exactPoint: intersection.clone(),
              type: 'plane',
              distance,
              ...(planeData.isMirror && { mirror: true }),
              ...(planeData.drawingSurfaceId && { drawingSurfaceId: planeData.drawingSurfaceId }),
            }
          }
        }
      }
    }
    return closestHit
  }

  // If occupancy or circle surface is active, only accept ground hits within occupancy and skip walls
  const occ: Occupancy2D | null = (surfaceOrOcc && (surfaceOrOcc as any).bitset)
    ? (surfaceOrOcc as Occupancy2D)
    : ((surfaceOrOcc && (surfaceOrOcc as any).kind) ? occupancyFromSurface(surfaceOrOcc as Surface, bounds as any) : null)

  if (occ) {
    let closestHit = closestPlaneHit([...mirrorPlanes, ...drawingSurfacePlanes])
    const plane = new Plane(new Vector3(0, 1, 0), 0)
    const intersection = new Vector3()
    if (mathRay.intersectPlane(plane, intersection)) {
      const epsilon = 0.0001
      let x = Math.floor(intersection.x + epsilon)
      let y = Math.floor(intersection.y + epsilon)
      let z = Math.floor(intersection.z + epsilon)
      x = Math.max(0, Math.min(bounds.x - 1, x))
      y = Math.max(0, Math.min(bounds.y - 1, y))
      z = Math.max(0, Math.min(bounds.z - 1, z))
      const hit = isInBounds({ x, y, z }, bounds)
        ? nearestOccupancyHit(occ, x, z, intersection, bounds, ray.origin)
        : null
      if (hit) {
        if (!closestHit || hit.distance < closestHit.distance) closestHit = hit
      }
    }
    return closestHit
  }

  const planes: PlaneHitTarget[] = [
    { normal: new Vector3(0, 1, 0), constant: 0, name: 'ground', clamp: (p) => Math.abs(p.y) <= 0.01 },
    { normal: new Vector3(1, 0, 0), constant: 0, name: 'west', clamp: (p) => Math.abs(p.x) <= 0.01 },
    { normal: new Vector3(-1, 0, 0), constant: bounds.x, name: 'east', clamp: (p) => Math.abs(p.x - bounds.x) <= 0.01 },
    { normal: new Vector3(0, 0, 1), constant: 0, name: 'south', clamp: (p) => Math.abs(p.z) <= 0.01 },
    { normal: new Vector3(0, 0, -1), constant: bounds.z, name: 'north', clamp: (p) => Math.abs(p.z - bounds.z) <= 0.01 },
    { normal: new Vector3(0, -1, 0), constant: bounds.y, name: 'top', clamp: (p) => Math.abs(p.y - bounds.y) <= 0.01 },
  ]

  return closestPlaneHit([...planes, ...mirrorPlanes, ...drawingSurfacePlanes])
}

export function raycastLayers(
  ray: Ray,
  voxelMaps: VoxelMap[],
  bounds: Position,
  surfaceOrOcc?: Surface | Occupancy2D,
  mirrors?: { x?: number; y?: number; z?: number },
  drawingSurfaces?: DrawingSurfaces,
  options?: RaycastPlaneOptions,
): Hit | null {
  let closestHit: Hit | null = null
  
  for (const voxels of voxelMaps) {
    const hit = raycastVoxels(ray, voxels, bounds)
    if (hit && (!closestHit || hit.distance < closestHit.distance)) {
      closestHit = hit
    }
  }
  
  const planeHit = raycastPlanes(ray, bounds, surfaceOrOcc, mirrors, drawingSurfaces, options)
  if (planeHit && (!closestHit || planeHit.distance < closestHit.distance)) {
    closestHit = planeHit
  }
  
  return closestHit
}


export const placementPosition = (hit: Hit, voxels: VoxelMap, bounds: Position): Position => {
  if (hit?.type !== 'voxel') {
    return hit.position
  }

  const adjacent = adjacentPlacementCenter(hit.position, hit.normal)
  
  if (isInBounds(adjacent, bounds) &&
      !voxels.has(posKeyXYZ(adjacent.x, adjacent.y, adjacent.z))) {
    return adjacent
  }
  
  return hit.position
}


// Compute the adjacent placement center (next cell along the surface normal)
export function adjacentPlacementCenter(hitPos: Vec3, normal: Vec3): Vec3 {
  return {
    x: hitPos.x + Math.round(normal.x),
    y: hitPos.y + Math.round(normal.y),
    z: hitPos.z + Math.round(normal.z),
  }
}
